import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["horizontal", "maintainVisibleContentPosition", "onScroll", "onTouchMove", "onWheel", "scrollEnabled", "scrollEventThrottle", "showsHorizontalScrollIndicator", "showsVerticalScrollIndicator", "style"];
/**
 * Copyright (c) Nicolas Gallagher.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 * 
 */

import * as React from 'react';
import StyleSheet from '../StyleSheet';
import View from '../View';
import useMergeRefs from '../../modules/useMergeRefs';
function normalizeScrollEvent(e) {
  return {
    nativeEvent: {
      contentOffset: {
        get x() {
          return e.target.scrollLeft;
        },
        get y() {
          return e.target.scrollTop;
        }
      },
      contentSize: {
        get height() {
          return e.target.scrollHeight;
        },
        get width() {
          return e.target.scrollWidth;
        }
      },
      layoutMeasurement: {
        get height() {
          return e.target.offsetHeight;
        },
        get width() {
          return e.target.offsetWidth;
        }
      }
    },
    timeStamp: Date.now()
  };
}
function shouldEmitScrollEvent(lastTick, eventThrottle) {
  var timeSinceLastTick = Date.now() - lastTick;
  return eventThrottle > 0 && timeSinceLastTick >= eventThrottle;
}

/**
 * Encapsulates the Web-specific scroll throttling and disabling logic
 */
var ScrollViewBase = /*#__PURE__*/React.forwardRef((props, forwardedRef) => {
  var horizontal = props.horizontal,
    maintainVisibleContentPosition = props.maintainVisibleContentPosition,
    onScroll = props.onScroll,
    onTouchMove = props.onTouchMove,
    onWheel = props.onWheel,
    _props$scrollEnabled = props.scrollEnabled,
    scrollEnabled = _props$scrollEnabled === void 0 ? true : _props$scrollEnabled,
    _props$scrollEventThr = props.scrollEventThrottle,
    scrollEventThrottle = _props$scrollEventThr === void 0 ? 0 : _props$scrollEventThr,
    showsHorizontalScrollIndicator = props.showsHorizontalScrollIndicator,
    showsVerticalScrollIndicator = props.showsVerticalScrollIndicator,
    style = props.style,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  var _ref = maintainVisibleContentPosition !== null && maintainVisibleContentPosition !== void 0 ? maintainVisibleContentPosition : {},
    mvcpMinIndexForVisible = _ref.minIndexForVisible,
    mvcpAutoscrollToTopThreshold = _ref.autoscrollToTopThreshold;
  var scrollState = React.useRef({
    isScrolling: false,
    scrollLastTick: 0
  });
  var scrollTimeout = React.useRef(null);
  var scrollRef = React.useRef(null);
  var prevFirstVisibleOffsetRef = React.useRef(null);
  var firstVisibleViewRef = React.useRef(null);
  var mutationObserverRef = React.useRef(null);
  var lastScrollOffsetRef = React.useRef(0);
  var getScrollOffset = React.useCallback(() => {
    if (scrollRef.current == null) {
      return 0;
    }
    return horizontal ? scrollRef.current.scrollLeft : scrollRef.current.scrollTop;
  }, [horizontal]);
  function createPreventableScrollHandler(handler) {
    return e => {
      if (scrollEnabled) {
        if (handler) {
          handler(e);
        }
      }
    };
  }
  function handleScroll(e) {
    e.stopPropagation();
    if (e.target === scrollRef.current) {
      e.persist();
      lastScrollOffsetRef.current = getScrollOffset();
      prepareForMaintainVisibleContentPosition();

      // A scroll happened, so the scroll resets the scrollend timeout.
      if (scrollTimeout.current != null) {
        clearTimeout(scrollTimeout.current);
      }
      scrollTimeout.current = setTimeout(() => {
        handleScrollEnd(e);
      }, 100);
      if (scrollState.current.isScrolling) {
        // Scroll last tick may have changed, check if we need to notify
        if (shouldEmitScrollEvent(scrollState.current.scrollLastTick, scrollEventThrottle)) {
          handleScrollTick(e);
        }
      } else {
        // Weren't scrolling, so we must have just started
        handleScrollStart(e);
      }
    }
  }
  function handleScrollStart(e) {
    scrollState.current.isScrolling = true;
    handleScrollTick(e);
  }
  function handleScrollTick(e) {
    scrollState.current.scrollLastTick = Date.now();
    if (onScroll) {
      onScroll(normalizeScrollEvent(e));
    }
  }
  function handleScrollEnd(e) {
    scrollState.current.isScrolling = false;
    if (onScroll) {
      onScroll(normalizeScrollEvent(e));
    }
  }
  var getContentView = React.useCallback(() => {
    var _scrollRef$current;
    return (_scrollRef$current = scrollRef.current) == null ? void 0 : _scrollRef$current.childNodes[0];
  }, []);
  var scrollToOffset = React.useCallback((offset, animated) => {
    var _scrollRef$current2;
    var behavior = animated ? 'smooth' : 'instant';
    (_scrollRef$current2 = scrollRef.current) == null || _scrollRef$current2.scroll(horizontal ? {
      left: offset,
      behavior
    } : {
      top: offset,
      behavior
    });
  }, [horizontal]);
  var prepareForMaintainVisibleContentPosition = React.useCallback(() => {
    if (mvcpMinIndexForVisible == null) {
      return;
    }
    var scrollNode = scrollRef.current;
    var contentView = getContentView();
    if (scrollNode == null || contentView == null) {
      return;
    }
    var scrollOffset = getScrollOffset();
    for (var i = mvcpMinIndexForVisible; i < contentView.childNodes.length; i++) {
      var subview = contentView.childNodes[i];
      var subviewOffset = horizontal ? subview.offsetLeft : subview.offsetTop;
      if (subviewOffset > scrollOffset || i === contentView.childNodes.length - 1) {
        prevFirstVisibleOffsetRef.current = subviewOffset;
        firstVisibleViewRef.current = subview;
        break;
      }
    }
  }, [getContentView, getScrollOffset, mvcpMinIndexForVisible, horizontal]);
  var adjustForMaintainVisibleContentPosition = React.useCallback(() => {
    if (mvcpMinIndexForVisible == null) {
      return;
    }
    var scrollNode = scrollRef.current;
    var firstVisibleView = firstVisibleViewRef.current;
    var prevFirstVisibleOffset = prevFirstVisibleOffsetRef.current;
    if (scrollNode == null || firstVisibleView == null || prevFirstVisibleOffset == null) {
      return;
    }
    var firstVisibleViewOffset = horizontal ? firstVisibleView.offsetLeft : firstVisibleView.offsetTop;
    var delta = firstVisibleViewOffset - prevFirstVisibleOffset;
    if (Math.abs(delta) > 0.5) {
      var scrollOffset = getScrollOffset();
      prevFirstVisibleOffsetRef.current = firstVisibleViewOffset;
      scrollToOffset(scrollOffset + delta, false);
      if (mvcpAutoscrollToTopThreshold != null && scrollOffset <= mvcpAutoscrollToTopThreshold) {
        scrollToOffset(0, true);
      }
    }
  }, [getScrollOffset, scrollToOffset, mvcpMinIndexForVisible, mvcpAutoscrollToTopThreshold, horizontal]);
  var setupMutationObserver = React.useCallback(() => {
    var _mutationObserverRef$;
    var scrollNode = scrollRef.current;
    var contentView = getContentView();
    if (contentView == null || scrollNode == null) {
      return;
    }
    (_mutationObserverRef$ = mutationObserverRef.current) == null || _mutationObserverRef$.disconnect();
    var mutationObserver = new MutationObserver(() => {
      // Chrome adjusts scroll position when elements are added at the top of the
      // view. We want to have the same behavior as react-native / Safari so we
      // reset the scroll position to the last value we got from an event.
      var lastScrollOffset = lastScrollOffsetRef.current;
      var scrollOffset = getScrollOffset();
      if (lastScrollOffset !== scrollOffset) {
        scrollToOffset(lastScrollOffset, false);
      }

      // This needs to execute after scroll events are dispatched, but
      // in the same tick to avoid flickering. rAF provides the right timing.
      requestAnimationFrame(() => {
        adjustForMaintainVisibleContentPosition();
      });
    });
    mutationObserver.observe(contentView, {
      attributes: true,
      childList: true,
      subtree: true
    });
    mutationObserverRef.current = mutationObserver;
  }, [adjustForMaintainVisibleContentPosition, getContentView, getScrollOffset, scrollToOffset]);
  React.useEffect(() => {
    prepareForMaintainVisibleContentPosition();
    setupMutationObserver();
  }, [prepareForMaintainVisibleContentPosition, setupMutationObserver]);
  var hideScrollbar = showsHorizontalScrollIndicator === false || showsVerticalScrollIndicator === false;
  var setMergedRef = useMergeRefs(scrollRef, forwardedRef);
  var onRef = React.useCallback(newRef => {
    // Make sure to only call refs and re-attach listeners if the node changed.
    if (newRef == null || newRef === scrollRef.current) {
      return;
    }
    setMergedRef(newRef);
    prepareForMaintainVisibleContentPosition();
    setupMutationObserver();
  }, [prepareForMaintainVisibleContentPosition, setMergedRef, setupMutationObserver]);
  React.useEffect(() => {
    var mutationObserver = mutationObserverRef.current;
    return () => {
      mutationObserver == null || mutationObserver.disconnect();
    };
  }, []);
  return /*#__PURE__*/React.createElement(View, _extends({}, rest, {
    onScroll: handleScroll,
    onTouchMove: createPreventableScrollHandler(onTouchMove),
    onWheel: createPreventableScrollHandler(onWheel),
    ref: onRef,
    style: [style, !scrollEnabled && styles.scrollDisabled, hideScrollbar && styles.hideScrollbar]
  }));
});

// Chrome doesn't support e.preventDefault in this case; touch-action must be
// used to disable scrolling.
// https://developers.google.com/web/updates/2017/01/scrolling-intervention
var styles = StyleSheet.create({
  scrollDisabled: {
    overflowX: 'hidden',
    overflowY: 'hidden',
    touchAction: 'none'
  },
  hideScrollbar: {
    scrollbarWidth: 'none'
  }
});
export default ScrollViewBase;